import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'
import { readBrandAccess } from '../lib/brandAccess'
import { setBrand as setSentryBrand } from '../lib/sentry'
import type { BrandConfig, BrandRole } from '../lib/types'
import type { AgencyBrand } from '../lib/agency'

// localStorage key holding the user's chosen active brand (for the switcher).
export const ACTIVE_BRAND_KEY = 'bmc_active_brand_id'

/** One brand the signed-in user belongs to — powers the brand switcher. */
export interface BrandSummary {
  id: string
  brand_name: string
  logo_url: string | null
  role: BrandRole
  is_owner: boolean
}

interface BrandContextType {
  brand: BrandConfig | null
  loading: boolean
  error: string | null
  /** The signed-in user's role on THIS (active) brand. */
  myRole: BrandRole | null
  /** True if the signed-in user is the protected account owner of the active brand. */
  isOwner: boolean
  /** Convenience: myRole === 'admin'. Gates team management + admin-only UI. */
  isAdmin: boolean
  /** admin or editor — can perform operational writes. Viewers are read-only.
   * Mirrors the worker's editor gate and the RLS write policies. */
  canEdit: boolean
  /** Every brand the signed-in user belongs to (for the switcher). */
  brands: BrandSummary[]
  /** Switch the active brand: persists the choice and hard-reloads to Home so
   * no brand-scoped data from the previous brand lingers. */
  switchBrand: (brandId: string) => void
  refreshBrand: () => Promise<void>
  updateBrand: (updates: Partial<BrandConfig>) => Promise<void>
  reload: () => Promise<void>
  /** Silent variant of reload: refetches brand without flipping `loading` to
   * true. Used during the onboarding-submit handoff so AuthenticatedApp doesn't
   * unmount the current screen and flash its loading dots between the
   * Onboarding submit state and BriefHub. */
  reloadSilent: () => Promise<void>
}

export const BrandContext = createContext<BrandContextType>({
  brand: null,
  loading: true,
  error: null,
  myRole: null,
  isOwner: false,
  isAdmin: false,
  canEdit: false,
  brands: [],
  switchBrand: () => {},
  refreshBrand: async () => {},
  updateBrand: async () => {},
  reload: async () => {},
  reloadSilent: async () => {},
})

export function useBrand() {
  return useContext(BrandContext)
}

/**
 * The ONE place a brand_configs row is written from the dashboard. Takes the
 * target brand id explicitly: the context exposes two different `updateBrand`
 * implementations (the anchor loader's and the active-brand override's), and a
 * write must never inherit "which brand" from whichever closure it came from.
 * That ambiguity is exactly what made an agency's Settings/Brief-settings save
 * land on the anchor brand while the UI showed another brand's data.
 */
async function writeBrandRow(brandId: string, updates: Partial<BrandConfig>): Promise<void> {
  const { error } = await supabase.from('brand_configs').update(updates).eq('id', brandId)
  if (error) throw error
}

export function useBrandLoader(userId: string | undefined) {
  const [brand, setBrand] = useState<BrandConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [myRole, setMyRole] = useState<BrandRole | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [brands, setBrands] = useState<BrandSummary[]>([])

  // First load of the page may reuse the shared brand_user_access read; every
  // subsequent load is an explicit refresh and must re-read it.
  const hasLoadedRef = useRef(false)

  const loadBrandInternal = useCallback(async (silent: boolean) => {
    if (!userId) return
    const isReload = hasLoadedRef.current
    hasLoadedRef.current = true
    if (!silent) setLoading(true)
    // NOTE: previously `setError(null)` fired here, eagerly clearing the
    // error before the load completed. That caused a UI flash on the
    // Onboarding-step-1 → step-2 transition: during the silent reload,
    // brand was still null AND error was already cleared, so
    // AuthenticatedApp's `isOnboarding` check (error==='no_brand_access' ||
    // brand && !onboarding_completed) momentarily returned false and
    // flipped to the regular dashboard for a frame before snapping back.
    // We now clear the error ONLY when a fresh load succeeds (below),
    // so the previous error sticks during the in-flight window.
    try {
      // A user can belong to MULTIPLE brands (invited to more than one). Load
      // every membership, then resolve the ACTIVE brand: the persisted choice
      // if it's still valid, else the default (owned → earliest joined, per the
      // query order below).
      // Shared with useAccountLoader, which needs the same rows moments later —
      // see lib/brandAccess.ts. `force` on every reload so a refresh always sees
      // current membership; only the first load of a page may be served warm.
      const { rows: accessRows, failed: accessError } = await readBrandAccess(userId, { force: isReload })

      if (accessError) {
        setError('Failed to load brand')
        if (!silent) setLoading(false)
        return
      }
      if (accessRows.length === 0) {
        setError('no_brand_access')
        setBrands([])
        if (!silent) setLoading(false)
        return
      }

      const ids = accessRows.map(r => r.brand_id)
      let activeId: string | null = null
      try { activeId = localStorage.getItem(ACTIVE_BRAND_KEY) } catch { /* storage disabled */ }
      if (!activeId || !ids.includes(activeId)) activeId = accessRows[0].brand_id
      const activeAccess = accessRows.find(r => r.brand_id === activeId) ?? accessRows[0]

      setMyRole(activeAccess.role)
      setIsOwner(activeAccess.is_owner)

      // Brand names + logos for the switcher list (all memberships).
      const { data: nameRows } = await supabase
        .from('brand_configs')
        .select('id, brand_name, logo_url')
        .in('id', ids)
      const byId = new Map(
        (nameRows ?? []).map((b: { id: string; brand_name: string; logo_url: string | null }) => [b.id, b])
      )
      setBrands(accessRows.map(r => ({
        id: r.brand_id,
        brand_name: byId.get(r.brand_id)?.brand_name ?? 'Brand',
        logo_url: byId.get(r.brand_id)?.logo_url ?? null,
        role: r.role ?? 'viewer',
        is_owner: r.is_owner,
      })))

      // Load the ACTIVE brand's full config. If that row turns out to be
      // unreadable (deleted, archived, or hidden by RLS) fall through to the
      // user's OTHER memberships instead of giving up: a single dangling
      // brand_user_access row must not strand a user who legitimately belongs to
      // another, healthy brand. Reporting brand_not_found here used to mean
      // "brand === null", which AuthenticatedApp then read as "brand-new user"
      // and answered with the signup wizard.
      const candidates = [
        activeAccess,
        ...accessRows.filter(r => r.brand_id !== activeAccess.brand_id),
      ]
      let brandData: BrandConfig | null = null
      let resolvedAccess = activeAccess
      for (const cand of candidates) {
        const { data } = await supabase
          .from('brand_configs')
          .select('*')
          .eq('id', cand.brand_id)
          .maybeSingle()
        if (data) {
          brandData = data as BrandConfig
          resolvedAccess = cand
          break
        }
      }

      if (!brandData) {
        setError('brand_not_found')
        if (!silent) setLoading(false)
        return
      }

      // Role/ownership must describe the brand we actually resolved, which is
      // not necessarily the one we started from.
      if (resolvedAccess !== activeAccess) {
        setMyRole(resolvedAccess.role)
        setIsOwner(resolvedAccess.is_owner)
        // Re-point the persisted selection so the dead brand isn't retried on
        // every load.
        try { localStorage.setItem(ACTIVE_BRAND_KEY, resolvedAccess.brand_id) } catch { /* storage disabled */ }
      }

      setBrand(brandData)
      // Clear any previous error now that we have a real brand row.
      // Important: this must come AFTER setBrand so the same render
      // commit picks up both state updates and isOnboarding never
      // evaluates against a brand-less, error-less context.
      setError(null)
      setSentryBrand({ id: brandData.id, brand_name: brandData.brand_name })
    } catch {
      setError('Failed to load brand')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [userId])

  const loadBrand = useCallback(() => loadBrandInternal(false), [loadBrandInternal])
  const loadBrandSilent = useCallback(() => loadBrandInternal(true), [loadBrandInternal])

  const switchBrand = useCallback((brandId: string) => {
    try { localStorage.setItem(ACTIVE_BRAND_KEY, brandId) } catch { /* storage disabled */ }
    // Hard reload to Home so no brand-scoped data (campaigns, audit, etc.) from
    // the previous brand lingers in memory.
    window.location.replace('/')
  }, [])

  useEffect(() => {
    loadBrand()
  }, [loadBrand])

  // Writes the ANCHOR brand — correct for solo brands and for the legacy
  // multi-brand switcher (which hard-reloads, so the anchor IS the selection).
  // For an agency inside a non-anchor brand this is NOT the brand on screen;
  // useEffectiveBrand replaces it with an active-brand-scoped version below.
  // Keyed on the id (not the whole row) so the identity stays stable across
  // ordinary field refreshes — BriefHub has it in a useCallback dep list.
  const brandId = brand?.id
  const updateBrand = useCallback(
    async (updates: Partial<BrandConfig>) => {
      if (!brandId) return
      await writeBrandRow(brandId, updates)
      setBrand((prev) => (prev && prev.id === brandId ? { ...prev, ...updates } : prev))
    },
    [brandId]
  )

  return {
    brand,
    loading,
    error,
    myRole,
    isOwner,
    isAdmin: myRole === 'admin',
    canEdit: myRole === 'admin' || myRole === 'editor',
    brands,
    switchBrand,
    refreshBrand: loadBrand,
    updateBrand,
    reload: loadBrand,
    reloadSilent: loadBrandSilent,
  }
}

/**
 * Re-scopes the brand context to the ACTIVE brand chosen in the agency switcher.
 * useBrandLoader loads the user's anchor brand; for agency accounts the user can
 * switch brands, and the whole dashboard (campaigns, stats, roster, briefs) must
 * then read THAT brand's data — not the anchor's. This swaps BrandContext.brand
 * to the active brand so every downstream query keys off the right brand_id.
 *
 * - Agency is at the lobby (inBrand === false) → no brand is "current" at all.
 *   `activeBrandId` persists across lobby visits (it's just "which brand to
 *   resume into next"), so it stays set and would otherwise keep this hook
 *   resolving a real brand's data even while the agency owner is looking at
 *   agency-level pages, not any single brand's dashboard. Every page/banner
 *   that reads brand.id (e.g. ProductIdErrorBanner, mounted once in App.tsx
 *   and never unmounted by an in-SPA lobby↔brand transition) would otherwise
 *   keep showing that brand's data at the lobby. Must return brand: null here.
 * - Active brand IS the anchor (or solo account) → pass through unchanged.
 * - A different brand in the account → load its full brand_configs row (real
 *   per-brand data; brands under an agency are all real DB rows).
 * - Row unreadable (shouldn't happen for a brand in the account) → keep the
 *   anchor rather than guessing.
 *
 * IMPORTANT: this must resolve the active brand EVEN WHEN `base.brand` is null.
 * A brandless agency (fresh signup, or just after adding its first brand) has no
 * anchor — useBrandLoader reports brand:null + error:'no_brand_access' and only
 * refetches on a userId change, so it stays null after add-brand/switch until a
 * manual reload. If we required a non-null anchor here, the dashboard would hang
 * on a permanent loader. So we key off `activeBrandId` alone and clear the
 * not-set-up error once the active brand resolves.
 */
export function useEffectiveBrand(
  base: BrandContextType,
  opts: {
    isAgency: boolean
    activeBrandId: string | null
    inBrand: boolean
    brands: AgencyBrand[]
    isAccountOwner?: boolean
  },
  userId?: string,
): BrandContextType {
  const { isAgency, activeBrandId, inBrand, isAccountOwner } = opts
  const baseBrand = base.brand
  const [override, setOverride] = useState<BrandConfig | null>(null)
  // The signed-in user's access row on the ACTIVE brand. Tagged with the brand
  // it describes so a stale row from the previously-active brand can't grant (or
  // withhold) rights on the new one mid-switch.
  const [overrideAccess, setOverrideAccess] = useState<
    { brandId: string; role: BrandRole | null; isOwner: boolean } | null
  >(null)
  // Bumped by the reload/reloadSilent wrappers below so a refresh refetches the
  // ACTIVE brand too, not just the anchor.
  const [refetchKey, setRefetchKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    // Nothing to override when: not an agency, at the lobby (not inside any
    // brand — nothing to fetch for), no active selection, or the anchor
    // already IS the active brand. Note the anchor may be null (brandless
    // agency) — in that case we still fetch the active brand below (as long
    // as inBrand is true).
    if (!isAgency || !inBrand || !activeBrandId || (baseBrand && activeBrandId === baseBrand.id)) {
      setOverride(null)
      setOverrideAccess(null)
      return
    }
    ;(async () => {
      // Both reads start together — the access row gates the same screens the
      // brand row fills in, so serialising them would just add a round trip.
      const rowPromise = supabase
        .from('brand_configs').select('*').eq('id', activeBrandId).maybeSingle()
      const accessPromise = userId
        ? supabase
            .from('brand_user_access').select('role, is_owner')
            .eq('brand_id', activeBrandId).eq('user_id', userId).maybeSingle()
        : null
      const { data } = await rowPromise
      const access = accessPromise ? (await accessPromise).data : null
      if (cancelled) return
      setOverride(data ? (data as BrandConfig) : null)
      const accessRow = access as { role?: string | null; is_owner?: boolean | null } | null
      setOverrideAccess({
        brandId: activeBrandId,
        role: accessRow ? ((accessRow.role as BrandRole) ?? 'viewer') : null,
        isOwner: Boolean(accessRow?.is_owner),
      })
    })()
    return () => { cancelled = true }
  }, [isAgency, inBrand, activeBrandId, baseBrand, userId, refetchKey])

  // Writes the ACTIVE brand. Keyed on activeBrandId alone — deliberately NOT on
  // the resolved `override` row, so a save fired during the resolve beat still
  // lands on the brand the user selected instead of being dropped.
  const updateActiveBrand = useCallback(async (updates: Partial<BrandConfig>) => {
    if (!activeBrandId) return
    await writeBrandRow(activeBrandId, updates)
    setOverride(prev => (prev && prev.id === activeBrandId ? { ...prev, ...updates } : prev))
  }, [activeBrandId])

  // base.reload/reloadSilent only refetch the ANCHOR, so on an agency's
  // non-anchor brand every "refresh brand state" caller (TikTok connect return,
  // CheckoutActivation poll, sidebar focus/wallet refresh) was refreshing a
  // brand the user isn't looking at. Refresh both.
  const baseReload = base.reload
  const baseReloadSilent = base.reloadSilent
  const reload = useCallback(async () => {
    setRefetchKey(k => k + 1)
    await baseReload()
  }, [baseReload])
  const reloadSilent = useCallback(async () => {
    setRefetchKey(k => k + 1)
    await baseReloadSilent()
  }, [baseReloadSilent])

  // Roles must describe the ACTIVE brand, not the anchor — an agency team member
  // can be admin on one brand and viewer on another. Fallbacks are deliberately
  // never *more* restrictive than the old anchor-role behavior: if the active
  // brand has no access row yet (the account_members → brand_user_access fan-out
  // hasn't landed on a just-added brand), an account owner is treated as admin
  // and everyone else keeps the anchor role, so this can't lock anyone out.
  const accessMatches = overrideAccess?.brandId === activeBrandId
  const scopedRole: BrandRole | null = accessMatches
    ? (overrideAccess?.role ?? (isAccountOwner ? 'admin' : base.myRole))
    : base.myRole
  const scopedIsOwner = accessMatches
    ? (overrideAccess?.role ? overrideAccess.isOwner : Boolean(isAccountOwner) || base.isOwner)
    : base.isOwner

  // Everything that must follow the ACTIVE brand rather than the anchor. Spread
  // AFTER `...base` in every override branch below.
  const scoped = {
    myRole: scopedRole,
    isOwner: scopedIsOwner,
    isAdmin: scopedRole === 'admin',
    canEdit: scopedRole === 'admin' || scopedRole === 'editor',
    updateBrand: updateActiveBrand,
    refreshBrand: reload,
    reload,
    reloadSilent,
  }

  // Agency at the lobby — no brand is "current," full stop, regardless of
  // whether activeBrandId/override resolve to a real row (activeBrandId just
  // remembers which brand to resume into, it does not mean the agency is
  // looking at it right now).
  if (isAgency && !inBrand) {
    return { ...base, brand: null }
  }

  // Only swap once the resolved override matches the current selection — avoids
  // briefly showing a stale brand's data mid-switch.
  if (override && override.id === activeBrandId) {
    // Clear the brandless-agency 'no_brand_access' signal: we DO have a brand now
    // (the active one), so downstream must not treat the user as un-set-up.
    return { ...base, ...scoped, brand: override, error: null }
  }
  // Agency is targeting a brand other than the anchor, and that target hasn't
  // resolved into `override` yet (still fetching, or fetching a brand we've
  // since switched away from again). Do NOT fall through to base.brand here —
  // it's the anchor brand, a completely unrelated brand under the same agency
  // account. Falling through would leak the anchor's data for a frame while
  // the real target brand loads — e.g. ProductIdErrorBanner briefly showing
  // ANOTHER brand's product-id error banner mid-switch, since it (and every
  // other page) keys its queries off `brand.id` and has no way to know that
  // id currently belongs to the wrong brand.
  //
  // Masking to null (not `loading: true`) is deliberate: every consumer
  // already handles brand:null (the brandless-agency case above proves it),
  // and NOT touching `loading` means this doesn't trip App.tsx's full-page
  // loading screen on every ordinary in-SPA brand switch — the switch stays
  // soft, it just shows no brand-scoped data for a beat instead of the wrong
  // brand's.
  //
  // `scoped` is spread here too: brand is masked, but a write fired in this beat
  // (or a role check made in it) must still target the brand the user selected,
  // never the anchor. The `baseBrand &&` guard is gone — a brandless agency has
  // no anchor at all, and its active brand still needs the scoped writers.
  if (isAgency && activeBrandId && activeBrandId !== baseBrand?.id) {
    return { ...base, ...scoped, brand: null }
  }
  // Not an agency, or the anchor IS the active brand: base already targets the
  // right row (the legacy multi-brand switcher hard-reloads, so the anchor and
  // the selection can't diverge there).
  return base
}
